import { KeychainAccess, SecureStorage } from "@aparajita/capacitor-secure-storage";
import { isNativeApp, toNativeApiUrl } from "./runtime";
import {
  getNativeRegistrationRegion,
  initializeNativeRegionRouting,
} from "./regionRouting";

const REFRESH_TOKEN_KEY = "refresh_token";
const STORAGE_PREFIX = "vaultgrid_native_auth_";
const REFRESH_TIMEOUT_MS = 12_000;
const ACCESS_TOKEN_EXPIRY_SKEW_MS = 30_000;

let accessToken = null;
let refreshToken = null;
let initialized = false;
let initializePromise = null;
let refreshPromise = null;

function emitAuthChanged(reason) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("vg:native-auth-changed", {
    detail: { authenticated: Boolean(accessToken || refreshToken), reason },
  }));
}

async function configureSecureStorage() {
  await SecureStorage.setKeyPrefix(STORAGE_PREFIX);
  await SecureStorage.setSynchronize(false);
  await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);
}

async function persistRefreshToken(value) {
  if (!isNativeApp()) return;
  if (value) {
    await SecureStorage.set(REFRESH_TOKEN_KEY, value);
  } else {
    await SecureStorage.remove(REFRESH_TOKEN_KEY).catch(() => false);
  }
}

async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function nativeJsonHeaders() {
  return {
    "Content-Type": "application/json",
    "X-VaultGrid-Client": "native",
    "X-VaultGrid-Registration-Region": getNativeRegistrationRegion(),
  };
}

function isAccessTokenUsable(token, nowMs = Date.now()) {
  const value = String(token || "").trim();
  if (!value) return false;

  try {
    const encodedPayload = value.split(".")[1];
    if (!encodedPayload) return false;
    const normalizedPayload = encodedPayload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, "=");
    const payload = JSON.parse(window.atob(normalizedPayload));
    const expiresAtMs = Number(payload?.exp || 0) * 1000;
    return Number.isFinite(expiresAtMs) && expiresAtMs > nowMs + ACCESS_TOKEN_EXPIRY_SKEW_MS;
  } catch {
    return false;
  }
}

export function getNativeAccessToken() {
  return isNativeApp() ? accessToken : null;
}

export function hasNativeRefreshToken() {
  return isNativeApp() && Boolean(refreshToken);
}

export async function setNativeAuthTokens(tokens, reason = "tokens_updated") {
  if (!isNativeApp()) return;
  const nextAccess = String(tokens?.access || "").trim();
  const nextRefresh = String(tokens?.refresh || refreshToken || "").trim();
  if (!nextAccess || !nextRefresh) {
    throw new Error("Native auth response did not include access and refresh tokens.");
  }

  accessToken = nextAccess;
  refreshToken = nextRefresh;
  await persistRefreshToken(nextRefresh);
  await initializeNativeRegionRouting({ accessToken: nextAccess });
  emitAuthChanged(reason);
}

export async function clearNativeAuthTokens(reason = "tokens_cleared") {
  accessToken = null;
  refreshToken = null;
  if (isNativeApp()) {
    await persistRefreshToken(null);
  }
  emitAuthChanged(reason);
}

export async function refreshNativeAccessToken({ force = false } = {}) {
  if (!isNativeApp()) return null;
  await initializeNativeAuth({ skipRefresh: true });
  if (!refreshToken) return null;
  // App resume (including returning from the iOS photo picker) can happen after
  // the short-lived access token expired. Do not hand that stale token to a
  // burst of foreground requests and wait for all of them to fail with 401.
  if (!force && isAccessTokenUsable(accessToken)) return accessToken;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    let response;
    try {
      response = await fetchWithTimeout(toNativeApiUrl("/accounts/token/refresh/"), {
        method: "POST",
        headers: nativeJsonHeaders(),
        body: JSON.stringify({ refresh: refreshToken }),
      });
    } catch (error) {
      // Keep the refresh token on transient network/offline failures.
      if (error?.name === "AbortError" || error instanceof TypeError) return null;
      throw error;
    }

    if (!response.ok) {
      if (response.status === 400 || response.status === 401) {
        await clearNativeAuthTokens("refresh_rejected");
      }
      return null;
    }

    const payload = await response.json();
    await setNativeAuthTokens(
      { access: payload?.access, refresh: payload?.refresh || refreshToken },
      "token_refreshed"
    );
    return accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function initializeNativeAuth({ skipRefresh = false } = {}) {
  if (!isNativeApp()) return null;
  if (initialized) {
    return skipRefresh ? accessToken : refreshNativeAccessToken();
  }
  if (initializePromise) return initializePromise;

  initializePromise = (async () => {
    await configureSecureStorage();
    const storedRefresh = await SecureStorage.get(REFRESH_TOKEN_KEY).catch(() => null);
    refreshToken = typeof storedRefresh === "string" ? storedRefresh : null;
    initialized = true;
    if (!skipRefresh && refreshToken) {
      await refreshNativeAccessToken({ force: true });
    }
    return accessToken;
  })().finally(() => {
    initializePromise = null;
  });

  return initializePromise;
}

export async function ensureNativeAccessToken() {
  if (!isNativeApp()) return null;
  await initializeNativeAuth();
  return accessToken || refreshNativeAccessToken({ force: true });
}

async function parseJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || payload?.detail || "Authentication failed.");
    error.response = { status: response.status, data: payload };
    throw error;
  }
  return payload;
}

export async function loginNative(credentials) {
  if (!isNativeApp()) return null;
  const response = await fetchWithTimeout(toNativeApiUrl("/accounts/token/"), {
    method: "POST",
    headers: nativeJsonHeaders(),
    body: JSON.stringify(credentials || {}),
  });
  const payload = await parseJsonResponse(response);
  await setNativeAuthTokens(payload, "login");
  return payload;
}

export async function exchangeNativeOAuthCode(code) {
  const response = await fetchWithTimeout(toNativeApiUrl("/accounts/native/oauth/exchange/"), {
    method: "POST",
    headers: nativeJsonHeaders(),
    body: JSON.stringify({ code }),
  });
  const payload = await parseJsonResponse(response);
  await setNativeAuthTokens(payload, "oauth_login");
  return payload;
}

export async function logoutNative() {
  if (!isNativeApp()) return;
  const tokenToRevoke = refreshToken;
  if (tokenToRevoke) {
    await fetchWithTimeout(toNativeApiUrl("/accounts/token/blacklist/"), {
      method: "POST",
      headers: nativeJsonHeaders(),
      body: JSON.stringify({ refresh: tokenToRevoke }),
    }).catch(() => null);
  }
  await clearNativeAuthTokens("logout");
  await initializeNativeRegionRouting();
}
